import { Inject, Injectable } from '@angular/core';
import { FlatTreeFunctions } from '@campus/utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { fetch, optimisticUpdate, pessimisticUpdate } from '@ngrx/router-store/data-persistence';
import { Store } from '@ngrx/store';
import { undo } from 'ngrx-undo-v2';
import { from } from 'rxjs';
import { map, mapTo } from 'rxjs/operators';
import { HistoryQueries } from '.';
import { EduContentBookInterface, EduContentTOCInterface, HistoryInterface, HistoryTypesEnum } from '../../+models';
import { HISTORY_SERVICE_TOKEN, HistoryServiceInterface } from '../../history/history.service.interface';
import { ActionSuccessful } from '../dal.actions';
import { DalState } from '../dal.state.interface';
import { EduContentBookQueries } from '../edu-content-book';
import { EduContentTocQueries } from '../edu-content-toc';
import { EffectFeedback, EffectFeedbackActions } from '../effect-feedback';
import { MethodQueries } from '../method';
import {
  DeleteHistory,
  ExercisedInToc,
  HistoryActionTypes,
  HistoryLoadError,
  HistoryLoaded,
  LoadHistory,
  StartUpsertHistory,
  UpsertHistory,
} from './history.actions';

@Injectable()
export class HistoryEffects {
  loadHistory$ = createEffect(() =>
    this.actions.pipe(
      ofType(HistoryActionTypes.LoadHistory),
      concatLatestFrom(() => this.store.select(HistoryQueries.getLoaded)),
      fetch({
        run: (action: LoadHistory, loaded: boolean) => {
          if (!action.payload.force && loaded) return;
          return this.historyService
            .getAllForUser(action.payload.userId)
            .pipe(map((history) => new HistoryLoaded({ history })));
        },
        onError: (action: LoadHistory, error) => {
          return new HistoryLoadError(error);
        },
      })
    )
  );

  startUpsertHistory$ = createEffect(() =>
    this.actions.pipe(
      ofType(HistoryActionTypes.StartUpsertHistory),
      pessimisticUpdate({
        run: (action: StartUpsertHistory) => {
          return this.historyService
            .upsertHistory(action.payload.history)
            .pipe(map((history) => new UpsertHistory({ history })));
        },
        onError: (action: StartUpsertHistory, error) => {
          // Feedback for failed add to history ?
        },
      })
    )
  );
  saveTocToHistory$ = createEffect(() =>
    this.actions.pipe(
      ofType(HistoryActionTypes.ExercisedInToc),
      concatLatestFrom(() => [
        this.store.select(EduContentBookQueries.getAllEntities),
        this.store.select(MethodQueries.getAllEntities),
        this.store.select(EduContentTocQueries.getTocsByBook),
      ]),
      map(([action, bookDict, methodDict, tocsByBookDict]) => {
        const { bookId, tocId } = (action as ExercisedInToc).payload;

        const paramToSave = { book: bookId, toc: tocId };
        const book = bookDict[bookId];
        const methodId = book.methodId;
        const learningAreaId = methodDict[methodId].learningAreaId;
        const name = this.getTocHistoryName(book, tocId, tocsByBookDict[bookId]);

        const history: HistoryInterface = {
          name,
          type: HistoryTypesEnum.METHOD,
          created: new Date(),
          criteria: JSON.stringify(paramToSave),
          learningAreaId,
        };
        return new StartUpsertHistory({ history });
      })
    )
  );

  deleteHistory$ = createEffect(() =>
    this.actions.pipe(
      ofType(HistoryActionTypes.DeleteHistory),
      optimisticUpdate({
        run: (action: DeleteHistory) => {
          return this.historyService
            .deleteHistory(action.payload.userId, action.payload.id)
            .pipe(mapTo(new ActionSuccessful({ successfulAction: 'delete history' })));
        },
        undoAction: (action: DeleteHistory, error) => {
          const undoAction = undo(action);
          const effectFeedback = EffectFeedback.generateErrorFeedback(
            this.uuid(),
            action,
            'Het is niet gelukt om het item uit jouw geschiedenis te verwijderen.'
          );

          const effectFeedbackAction = new EffectFeedbackActions.AddEffectFeedback({ effectFeedback });
          return from([undoAction, effectFeedbackAction]);
        },
      })
    )
  );

  private getTocHistoryName(book: EduContentBookInterface, tocId: number, tocs: EduContentTOCInterface[]): string {
    const ancestors = FlatTreeFunctions.getAllAncestorsForNode<EduContentTOCInterface>(tocId, tocs, {
      includeSelf: true,
    });
    const name = ancestors.reduce((acc, toc) => {
      return `${acc} > ${toc.title}`;
    }, `${book?.title}`);
    return name;
  }

  constructor(
    @Inject('uuid') private uuid: () => string,
    private actions: Actions,
    private store: Store<DalState>,
    @Inject(HISTORY_SERVICE_TOKEN)
    private historyService: HistoryServiceInterface
  ) {}
}
